var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { shadow, hideScrollbar } from '../../../../styles/style-mixins';
const MOBILE_AUTOSTRETCH_ITEMS_MAX = 3;
const DEFAULT_HEIGHT = '48px';
const Wrapper = styled.div(({ theme }) => css `
    ${shadow(theme)};
    ${hideScrollbar()}
    background: var(--cui-bg-normal);
    height: ${DEFAULT_HEIGHT};
    display: flex;
    overflow-x: auto;
  `);
const navigationBaseStyles = css `
  display: flex;
  flex-wrap: nowrap;
`;
const stretchedStyles = ({ children, theme }) => css `
  width: 100%;

  & [role='tab'] {
    flex: 1 1 auto;
    padding: 0 ${theme.spacings.kilo};
    width: ${Math.floor(100 / children.length)}%;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;
const navigationChildrenStyles = (_a) => {
    var { stretched } = _a, props = __rest(_a, ["stretched"]);
    return stretched && stretchedStyles(props);
};
const navigationResponsiveChildrenStyles = (props) => props.children.length <= MOBILE_AUTOSTRETCH_ITEMS_MAX &&
    css `
    ${props.theme.mq.untilKilo} {
      ${stretchedStyles(props)};
    }
  `;
const Navigation = styled.div(navigationBaseStyles, navigationChildrenStyles, navigationResponsiveChildrenStyles);
/**
 * TabList component that wraps the Tab components
 */
export function TabList(_a) {
    var { className, style } = _a, props = __rest(_a, ["className", "style"]);
    return (_jsx(Wrapper, Object.assign({ className: className, style: style }, { children: _jsx(Navigation, Object.assign({}, props, { role: "tablist" })) })));
}
TabList.propTypes = {
    /**
     * Override styles for the TabList component.
     */
    className: PropTypes.string,
    style: PropTypes.object,
};
